import * as Sentry from "@sentry/browser";
import { produce } from "immer";

import { initialAgenticAiState } from "./agenticAiState";
import type { AgenticAiAction, AgenticAiState } from "./types";

export const agenticAiReducer = (
  prevState: AgenticAiState | undefined = initialAgenticAiState,
  action: AgenticAiAction
): AgenticAiState => {
  return produce(prevState, (state) => {
    switch (action.type) {
      case "agenticAi/setSessionId":
        state.sessionId = action.sessionId;
        break;
      case "agenticAi/openChat":
        state.isOpen = true;
        if (action.withSupportEntryPoint) {
          state.supportEntryPoint = true;
        }
        if (action.dynamicPromptsContext !== undefined) {
          state.dynamicPromptsContext = action.dynamicPromptsContext;
        } else {
          state.dynamicPromptsContext = undefined;
        }
        break;
      case "agenticAi/closeChat":
        state.isOpen = false;
        // Reset the support entry point flag when the chat is closed;
        // this allows users to get dynamic prompts again, or to re-enable the flag by
        // opening the chat from the support menu again.
        state.supportEntryPoint = false;
        state.dynamicPromptsContext = undefined;
        break;
      case "agenticAi/registerTool":
        if (action.key in state.frontendTools) {
          throw new Error(`Tool ${action.key} already registered`);
        }

        if (action.tool.definition.name in state.frontendToolNames) {
          const error = new Error(`Tool ${action.tool.definition.name} already registered`);
          console.error(error);
          Sentry.captureException(error, { level: "error" });
          state.frontendToolNames[action.tool.definition.name].push(action.key);
        } else {
          state.frontendToolNames[action.tool.definition.name] = [action.key];
        }

        state.frontendTools[action.key] = action.tool;
        break;
      case "agenticAi/unregisterTool":
        if (state.frontendTools[action.key]) {
          const name = state.frontendTools[action.key].definition.name;
          delete state.frontendTools[action.key];

          state.frontendToolNames[name] = state.frontendToolNames[name].filter((key) => key !== action.key);
          if (state.frontendToolNames[name].length === 0) {
            delete state.frontendToolNames[name];
          }
        }
        break;
      case "agenticAi/registerContext":
        if (action.key in state.context) {
          throw new Error(`Context ${action.key} already registered`);
        }

        if (action.identifier in state.contextIdentifiers) {
          if (!action.options.ignoreDuplicates) {
            const error = new Error(`Context with identifier "${action.identifier}" already registered`);
            console.error(error);
            Sentry.captureException(error, { level: "error" });
          }
          state.contextIdentifiers[action.identifier].push(action.key);
        } else {
          state.contextIdentifiers[action.identifier] = [action.key];
        }

        state.context[action.key] = {
          value: action.value,
          identifier: action.identifier,
          options: action.options,
        };
        break;
      case "agenticAi/unregisterContext": {
        if (state.context[action.key]) {
          const identifier = state.context[action.key].identifier;
          delete state.context[action.key];

          state.contextIdentifiers[identifier] = state.contextIdentifiers[identifier].filter(
            (key) => key !== action.key
          );
          if (state.contextIdentifiers[identifier].length === 0) {
            delete state.contextIdentifiers[identifier];
          }
        }
        break;
      }
      case "agenticAi/registerLiquidField":
        // Upsert: update if exists, insert if not
        state.liquidFields[action.key] = action.field;
        break;
      case "agenticAi/unregisterLiquidField":
        if (state.liquidFields[action.key]) {
          delete state.liquidFields[action.key];
        }
        break;
      case "agenticAi/clearSupportEntryPoint":
        state.supportEntryPoint = false;
        break;
    }
  });
};
